import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, LineChart, Line } from 'recharts';
import { TopicsData } from '../hooks/useTopicsData';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import { useFilterContext } from '../context/FilterContext';
import { SectionCard, SectionHeader, SectionLoading, NestedPanel, LegendItem } from './ui/Section';
import { useChartStyles, ChartTooltipCard } from './ui/chartStyles';
import { ShareDonut, RankBarChart, formatCompact } from './ui/charts';

const BRAND_BREAKDOWN_INFO =
  'Brand Breakdown shows how brands compare across content metrics. Snapshot shows Share of Voice (post volume), Share of Engagement (total engagement), and Average Engagement per Post. The three Trend views show how these metrics change over time with daily, weekly, or monthly granularity.';

interface BrandBreakdownProps {
  data: TopicsData | null;
  selectedBrands: string[];
  selectedPlatforms: string[];
  selectedPillars: string[];
}

// View modes for the Brand Breakdown component
type ViewMode = 'snapshot' | 'volume' | 'engagement' | 'aepp';
// Time granularity for trend charts
type TimeGranularity = 'daily' | 'weekly' | 'monthly';

const BrandBreakdown: React.FC<BrandBreakdownProps> = ({ 
  data,
  selectedBrands, 
  selectedPlatforms, 
  selectedPillars
}) => {
  // State for view mode (snapshot, volume trend, engagement trend, or AEPP trend)
  const [viewMode, setViewMode] = useState<ViewMode>('snapshot');
  // State for time granularity (day, week, month)
  const [timeGranularity, setTimeGranularity] = useState<TimeGranularity>('daily');

  // Get brand colors from context
  const { brandColors, brandColorsLoading } = useFilterContext();
  const cs = useChartStyles();

  if (!data) {
    return null;
  }

  // Show loading state while brand colors are loading
  if (brandColorsLoading) {
    return (
      <SectionCard>
        <SectionHeader title="Brand Breakdown" info={BRAND_BREAKDOWN_INFO} />
        <SectionLoading label="Loading brand colors..." />
      </SectionCard>
    );
  }

  // Handle view mode change
  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Handle time granularity change
  const handleTimeGranularityChange = (
    event: React.MouseEvent<HTMLElement>,
    newGranularity: TimeGranularity,
  ) => {
    if (newGranularity !== null) {
      setTimeGranularity(newGranularity);
    }
  };

  // Filter data based on selectedBrands and selectedPlatforms
  const filterBrandData = (data: any[]) => {
    let filtered = data;
    
    // Filter by brands if any selected
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(item => selectedBrands.includes(item.Brand));
    }
    
    // Filter by platforms if any selected
    if (selectedPlatforms.length > 0) {
      filtered = filtered.filter(item => selectedPlatforms.includes(item.Platform));
    }
    
    return filtered;
  };

  // Transform brand share of voice data for the pie chart
  const voiceData = filterBrandData(data.brand_share_of_voice)
    .reduce((acc: { [key: string]: number }, item) => {
      // Sum up post counts for each brand across all platforms
      acc[item.Brand] = (acc[item.Brand] || 0) + item.post_count;
      return acc;
    }, {});

  // Convert to array format and add colors
  const voiceDataArray = Object.entries(voiceData).map(([brand, count], index) => ({
    name: brand,
    value: count,
    rawValue: count, // Store the raw count
    color: brandColors[brand] || `hsl(${index * 45}, 70%, 50%)`
  }));

  // Calculate percentages for share of voice
  const totalPosts = voiceDataArray.reduce((sum, item) => sum + item.value, 0);
  const voiceDataWithPercentages = voiceDataArray.map(item => ({
    ...item,
    value: (item.value / totalPosts) * 100
  })).sort((a, b) => b.value - a.value); // Sort by percentage in descending order

  // Transform brand share of engagement data for the pie chart
  const engagementData = filterBrandData(data.brand_share_of_engagement)
    .reduce((acc: { [key: string]: number }, item) => {
      // Sum up engagement scores for each brand across all platforms
      acc[item.Brand] = (acc[item.Brand] || 0) + item.post_engscore;
      return acc;
    }, {});

  // Convert to array format and add colors
  const engagementDataArray = Object.entries(engagementData).map(([brand, score], index) => ({
    name: brand,
    value: score,
    rawValue: score, // Store the raw score
    color: brandColors[brand] || `hsl(${index * 45}, 70%, 50%)`
  }));

  // Calculate percentages for share of engagement
  const totalEngagement = engagementDataArray.reduce((sum, item) => sum + item.value, 0);
  const engagementDataWithPercentages = engagementDataArray.map(item => ({
    ...item,
    value: (item.value / totalEngagement) * 100
  })).sort((a, b) => b.value - a.value); // Sort by percentage in descending order

  // Custom tooltip component
  // Helper function to format numbers with 'k' notation
  const formatNumberWithK = (value: number): string => {
    const roundedValue = Math.round(value);
    if (roundedValue >= 1000) {
      return Math.round(roundedValue / 1000) + 'k';
    }
    return roundedValue.toString();
  };

  // Transform average engagement per post data for the bar chart
  const averageEngagementData = (() => {
    // Start with the raw data
    const filteredData = filterBrandData(data.brand_average_engagement_per_post);
    
    // Group by brand, summing engagement and counts across selected platforms
    const brandTotals = filteredData.reduce((acc: Record<string, { totalEngagement: number, postCount: number }>, item) => {
      if (!acc[item.Brand]) {
        acc[item.Brand] = { totalEngagement: 0, postCount: 0 };
      }
      
      // Sum the engagement and post counts
      acc[item.Brand].totalEngagement += item.total_engagement;
      acc[item.Brand].postCount += item.post_count;
      
      return acc;
    }, {});

    // Calculate average per brand and convert to chart format
    return Object.entries(brandTotals)
      .map(([brand, data]) => ({
        name: brand,
        value: data.postCount > 0 ? data.totalEngagement / data.postCount : 0
      }))
      .sort((a, b) => b.value - a.value) // Sort by value in descending order
      .slice(0, 10); // Take top 10
  })();

  // Process trend data for line charts
  const prepareTrendData = (trendData: any, granularity: TimeGranularity, selectedBrands: string[]) => {
    if (!trendData || !trendData[granularity]) return [];
    
    const data = trendData[granularity];
    
    // Filter to only include selected brands or all if none selected
    const filteredData = data.map((item: any) => {
      const newItem: any = { date: item.date };
      
      // Add all brand data if no brands are selected, otherwise filter
      if (selectedBrands.length === 0) {
        // Include all brands
        Object.keys(item).forEach(key => {
          if (key !== 'date') {
            newItem[key] = item[key];
          }
        });
      } else {
        // Only include selected brands
        selectedBrands.forEach(brand => {
        if (item[brand] !== undefined) {
          newItem[brand] = item[brand];
        }
      });
      }
      
      return newItem;
    });
    
    return filteredData;
  };
  
  // Prepare trend data for different metrics
  const volumeTrendData = prepareTrendData(data.volume_trend, timeGranularity, selectedBrands);
  const engagementTrendData = prepareTrendData(data.engagement_trend, timeGranularity, selectedBrands);
  const avgEngagementTrendData = prepareTrendData(data.avg_engagement_trend, timeGranularity, selectedBrands);

  // Render trend chart based on current view mode
  const renderTrendChart = () => {
    let chartData: any[] = [];
    let title = '';
    let yAxisLabel = '';
    
    switch (viewMode) {
      case 'volume':
        chartData = volumeTrendData;
        title = 'Volume Trend';
        yAxisLabel = 'Post Count';
        break;
      case 'engagement':
        chartData = engagementTrendData;
        title = 'Engagement Trend';
        yAxisLabel = 'Engagement Score';
        break;
      case 'aepp':
        chartData = avgEngagementTrendData;
        title = 'Average Engagement per Post Trend';
        yAxisLabel = 'Avg. Engagement';
        break;
      default:
        return null;
    }
    
    // Get all brand keys (excluding 'date')
    const brandKeys = chartData.length > 0 
      ? Object.keys(chartData[0]).filter(key => key !== 'date')
      : [];

    // Helper function to format numbers with 'k' notation (use the shared function)
    const formatYAxisNumber = formatCompact;

    // Calculate domain for Y-axis based on actual data
    const allValues: number[] = [];
    chartData.forEach(item => {
      brandKeys.forEach(brand => {
        if (typeof item[brand] === 'number') {
          allValues.push(item[brand]);
        }
      });
    });
    
    const minValue = allValues.length > 0 ? Math.min(...allValues) : 0;
    
    // Calculate custom ticks starting from 0
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 100;
    const tickCount = 6;
    
    // Smart scaling based on the data range - don't force 1000s scale for small values
    let maxTick: number;
    if (maxValue <= 100) {
      maxTick = Math.ceil(maxValue / 10) * 10; // Round up to nearest 10
    } else if (maxValue <= 1000) {
      maxTick = Math.ceil(maxValue / 100) * 100; // Round up to nearest 100
    } else {
      maxTick = Math.ceil(maxValue / 1000) * 1000; // Round up to nearest 1000
    }
    
    const tickInterval = maxTick / (tickCount - 1);
    const customTicks = Array.from({ length: tickCount }, (_, i) => i * tickInterval);

    // If there are negative values, we'll handle this in the chart rendering
    const hasNegativeValues = minValue < 0;
    
    return (
      <NestedPanel>
        <SectionHeader
          title={title}
          level="sub"
          actions={
            <ToggleButtonGroup
              value={timeGranularity}
              exclusive
              onChange={handleTimeGranularityChange}
              aria-label="time granularity"
              size="small"
            >
              <ToggleButton value="daily">Day</ToggleButton>
              <ToggleButton value="weekly">Week</ToggleButton>
              <ToggleButton value="monthly">Month</ToggleButton>
            </ToggleButtonGroup>
          }
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1, mb: 2 }}>
          {brandKeys.map((brand, index) => (
            <LegendItem key={brand} color={brandColors[brand] || `hsl(${index * 45}, 70%, 50%)`} label={brand} />
          ))}
        </Box>
        <Box sx={{ height: 460 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 16, left: 8, bottom: hasNegativeValues ? 30 : 8 }}
          >
            <CartesianGrid {...cs.grid} />
            <XAxis {...cs.xAxis} dataKey="date" minTickGap={24} />
            <YAxis
              {...cs.yAxis}
              width={64}
              domain={[0, maxTick]}
              ticks={customTicks}
              type="number"
              tickFormatter={formatYAxisNumber}
              label={cs.axisLabel(yAxisLabel)}
            />
            <RechartsTooltip
              cursor={cs.lineCursor}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  // Sort payload by value in descending order
                  const sortedPayload = [...payload].sort((a, b) => {
                    const aValue = (a as any).value || 0;
                    const bValue = (b as any).value || 0;
                    return bValue - aValue;
                  });

                  return (
                    <ChartTooltipCard
                      title={label}
                      rows={sortedPayload.map((entry: any) => ({
                        key: entry.name,
                        color: entry.color,
                        label: entry.name,
                        value:
                          viewMode === 'engagement' || viewMode === 'aepp'
                            ? (entry.value >= 1000 ? formatNumberWithK(entry.value) : entry.value.toFixed(2))
                            : formatNumberWithK(entry.value),
                      }))}
                    />
                  );
                }
                return null;
              }}
            />
            {brandKeys.map((brand, index) => (
              <Line
                key={brand}
                type="monotone"
                dataKey={brand}
                name={brand}
                stroke={brandColors[brand] || `hsl(${index * 45}, 70%, 50%)`}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        </Box>
      </NestedPanel>
    );
  };

  // Render the snapshot view: two share donuts and the per-post ranking
  const renderSnapshotView = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <ShareDonut
        title="Share of Voice"
        data={voiceDataWithPercentages}
        rawLabel="Posts"
        formatRaw={(n) => Math.round(n).toLocaleString()}
        centerValue={formatCompact(totalPosts)}
        centerCaption="posts"
      />
      <ShareDonut
        title="Share of Engagement"
        data={engagementDataWithPercentages}
        rawLabel="Engagement"
        formatRaw={formatNumberWithK}
        centerValue={formatCompact(totalEngagement)}
        centerCaption="engagement"
      />
      <NestedPanel sx={{ gridColumn: { md: '1 / -1' } }}>
        <SectionHeader title="Average engagement per post, top 10" level="sub" />
        <RankBarChart
          data={averageEngagementData.map((d, index) => ({
            ...d,
            color: brandColors[d.name] || `hsl(${index * 45}, 70%, 50%)`,
          }))}
          formatValue={(v) => (v >= 1000 ? formatCompact(v) : v === 0 ? '0' : v.toFixed(v < 10 ? 2 : 0))}
          tooltipLabel="Avg. engagement"
        />
      </NestedPanel>
    </Box>
  );

  return (
    <SectionCard>
      <SectionHeader
        title="Brand Breakdown"
        info={BRAND_BREAKDOWN_INFO}
        actions={
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            size="small"
          >
            <ToggleButton value="snapshot">Snapshot</ToggleButton>
            <ToggleButton value="volume">Volume Trend</ToggleButton>
            <ToggleButton value="engagement">Engagement Trend</ToggleButton>
            <ToggleButton value="aepp">AEPP Trend</ToggleButton>
          </ToggleButtonGroup>
        }
      />

      {/* Render the appropriate view based on the selected view mode */}
      {viewMode === 'snapshot' ? renderSnapshotView() : renderTrendChart()}
    </SectionCard>
  );
};

export default BrandBreakdown; 
